import React, { useEffect, useState } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import { useFormik } from "formik"
import * as Yup from "yup"
import { useNavigate, useParams } from 'react-router-dom'
import moment from 'moment'

// components
import AppCard from '../../components/AppCard'
import AppForm from '../../components/form/form-game-voucher/AppForm'
import ActionButtonForm from 'src/components/form/general-input-form/ActionButtonForm'
import AppModalConfirmation from '../../components/modal/AppModalConfirmation'

// helpers
import Axios from '../../helpers/axios'
import { singleGamesOptionsSelect } from '../../helpers/format-options'

const EditGameVoucher = () => {
  const navigate = useNavigate()
  const dispatch = useDispatch()
  const params = useParams()
  const { id } = params

  const isLoading = useSelector((state) => state.isLoading)

  const [gameOptions, setGamesOptions] = useState(false)

  const [gameVoucherDetail, setGameVoucherDetail] = useState(null)
  const [showModalConfirm, setShowModalConfirm] = useState({ show: false })

  const formik = useFormik({
    initialValues: {
      gamevoucher_voucher_code: gameVoucherDetail?.voucher_code || '',
      gamevoucher_is_used: gameVoucherDetail?.is_used || false,
      gamevoucher_expired_date: gameVoucherDetail?.expired_date || '',
      gamevoucher_game_value: {
        label: gameVoucherDetail?.game?.game_name || '',
        value: gameVoucherDetail?.game_id || ''
      }
    },
    validationSchema: Yup.object().shape({
      gamevoucher_voucher_code: Yup.string()
        .required("Please enter voucher code."),
      gamevoucher_expired_date: Yup.string()
        .required("Please enter expired date."),
      gamevoucher_game_value: Yup.object().shape({
        label: Yup.string().required('Please select game name.')
      }).required('Please select game name.')
    }),
    enableReinitialize: true,
    validateOnBlur: false,
    validateOnChange: false,
    onSubmit: async (values, { setSubmitting }) => {
      handleSave(values)
      setSubmitting(false)
    },
  });

  useEffect(() => {
    fetchDetailGameVoucher()
    fetchAllGames()
  }, [id])

  const fetchDetailGameVoucher = () => {
    dispatch({
      type: 'SET_IS_LOADING',
      isLoading: true
    })
    Axios({
      method: "get",
      url: `/api/gamepass/v1/voucher/game/detail/${id}`,
    })
      .then(({ data }) => {
        if (data?.success) {
          setGameVoucherDetail(data?.value)
        }
      })
      .catch((err) => {
        console.error(err);
      })
      .finally(() => {
        dispatch({
          type: 'SET_IS_LOADING',
          isLoading: false
        })
      })
  }

  const fetchAllGames = () => {
    Axios({
      method: "get",
      url: "/api/gamepass/v1/game/list",
    })
      .then(({ data }) => {
        if (data?.success) {
          setGamesOptions(singleGamesOptionsSelect(data.value))
        }
      })
      .catch((err) => {
        console.error(err);
      });
  }

  const handleInputValue = (values) => {
    let data = {
      voucher_code: values.gamevoucher_voucher_code,
      is_used: values.gamevoucher_is_used,
      expired_date: values.gamevoucher_expired_date,
      game_id: values.gamevoucher_game_value.value
    }

    return data
  }

  const handlePost = (payload) => {
    dispatch({
      type: 'SET_IS_LOADING',
      isLoading: true
    })
    Axios({
      method: "put",
      url: `/api/gamepass/v1/voucher/game/update/${id}`,
      data: payload
    })
      .then(({ data }) => {
        if (data?.success) {
          navigate(`/game-voucher/details/${id}`)
        }
      })
      .catch((err) => {
        console.error(err);
      })
      .finally(() => {
        dispatch({
          type: 'SET_IS_LOADING',
          isLoading: false
        })
      })
  }

  const handleSave = (values) => {
    const inputPayload = handleInputValue(values)
    handlePost(inputPayload)
  }

  const handleSubmit = () => {
    formik.handleSubmit()

    setShowModalConfirm({ show: false })
  }

  const handleSwitch = (e, name) => {
    let checked = e.target.checked

    formik.setValues((prevValues) => ({
      ...prevValues,
      [name]: checked
    }), false)
  }

  const handleDateChange = (date, name) => {
    formik.setValues((values) => ({
      ...values,
      [name]: moment(date).format('YYYY-MM-DD HH:mm:ss')
    }), false)
  }

  const handleSelectGame = (data) => {
    formik.setValues((values) => ({
      ...values,
      gamevoucher_game_value: data
    }), false)
  }

  return (
    <>
      {
        showModalConfirm.show && (
          <AppModalConfirmation 
            confirmationType="confirmation"
            showModal={showModalConfirm}
            setShowModal={setShowModalConfirm}
            headerTitle="Confirmation"
            descriptionText="Are you sure want to edit this game voucher?"
            handleSubmit={handleSubmit}
            disabled={formik.isSubmitting}
            isLoading={isLoading}
          />
        )
      }
      <AppCard 
        className='mb-4'
        headerTitle={<h4>Edit Game Voucher</h4>}
        bodyContent={
          <AppForm 
            values={formik.values}
            handleChange={formik.handleChange}
            errors={formik.errors}
            handleSwitch={handleSwitch}
            onDateChange={handleDateChange}
            gameOptions={gameOptions}
            handleSelectGame={handleSelectGame}
          />
        }
        footerContent={
          <ActionButtonForm
            isLoading={isLoading}
            disabled={formik.isSubmitting}
            onCancel={() => navigate('/game-voucher/list')}
            onSave={() => setShowModalConfirm({ show: true })}
          />
        }
      />
    </>
  )
}

export default EditGameVoucher